import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useContent } from "../content-context";
import { Link } from "react-router-dom";
import { scrollToSection } from "../lib/scroll";
import { EASE, MaskedLine } from "./Reveal";

export default function Hero({ ready }) {
  const { hero } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
      <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="letterbox relative flex h-[100svh] min-h-[640px] flex-col overflow-hidden bg-navy"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" data-testid="hero-media">
        <img
          src={hero.image}
          alt={hero.imageAlt}
          className="h-full w-full object-cover anim-hero-zoom"
          data-testid="hero-media-image"
        />
        <div className="absolute inset-0 bg-navy/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/10 to-navy" />
      </motion.div>

      <motion.div
        style={{ opacity: contentFade }}
        className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-center px-5 pt-24 md:px-10"
      >
        <motion.p
          className="mb-6 flex items-center gap-4 text-[10px] font-medium uppercase tracking-widest2 text-gold-2 md:text-xs"
          initial={{ opacity: 0, y: 14 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-12 bg-gold" />
          {hero.eyebrow}
        </motion.p>

        <h1
          className="font-display text-[clamp(3rem,9vw,8.5rem)] font-medium leading-[0.98] text-ivory"
          data-testid="hero-heading"
        >
          {hero.titleLines.map((line, i) => (
            <MaskedLine key={line.text} delay={0.45 + i * 0.16} ready={ready}>
              <span className={line.italic ? "italic text-gold-2" : ""}>{line.text}</span>
            </MaskedLine>
          ))}
          <span className="sr-only">in Westport, County Mayo</span>
        </h1>

        <motion.p
          className="mt-8 font-display text-xl italic text-gold-3 md:text-2xl"
          initial={{ opacity: 0, y: 18 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
          data-testid="hero-tagline"
        >
          {hero.tagline}
        </motion.p>

        <motion.p
          className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/85 md:text-base"
          initial={{ opacity: 0, y: 18 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
          data-testid="hero-body"
        >
          {hero.body}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          initial={{ opacity: 0, y: 18 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: EASE, delay: 1.35 }}
        >
          <Link
            to={hero.cta1.to}
            data-testid="hero-explore-button"
            className="group relative overflow-hidden bg-gold px-9 py-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-500 hover:bg-gold-2 sm:text-left"
          >
            <span className="relative z-10">{hero.cta1.label}</span>
          </Link>
          <Link
            to={hero.cta2.to}
            data-testid="hero-enquire-button"
            className="group border border-ivory/40 px-9 py-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-all duration-500 hover:border-gold hover:text-gold-2 sm:text-left"
          >
            {hero.cta2.label}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="relative z-10 flex items-center justify-between px-5 pb-8 pr-20 md:px-10 md:pr-24"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.7 }}
      >
        <a
          href="#island"
          onClick={(e) => { e.preventDefault(); scrollToSection("#island"); }}
          data-testid="hero-scroll-cue"
          className="flex items-center gap-3 text-[10px] uppercase tracking-widest2 text-ivory/70 transition-colors hover:text-gold-2"
          aria-label="Scroll to explore"
        >
          <ChevronDown className="h-4 w-4 anim-scroll-cue" />
          Scroll
        </a>
        <p className="hidden text-[10px] uppercase tracking-widest2 text-ivory/50 sm:block">
          53°47′N — 9°26′W · Atlantic Ocean
        </p>
      </motion.div>
    </section>
  );
}
